import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { fetchProfitLoss } from "@/shared/api/accounting";
import { AdminList, AdminListRow } from "@/modules/shell/AdminList";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

export function ProfitLossPage() {
  const { companySlug } = useParams();

  const plQuery = useQuery({
    queryKey: queryKeys.gl.profitLoss(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => fetchProfitLoss(companySlug!),
  });
  const errorMsg = queryErrorMessage(plQuery.error, plQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  if (!companySlug) return null;

  const data = plQuery.data ?? null;
  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  return (
    <PageFrame width="narrow">
      <PageHeader
        eyebrow={
          <Link
            to={`/c/${companySlug}/gl/trial-balance`}
            className="text-inherit no-underline hover:underline"
          >
            Trial balance
          </Link>
        }
        title="Profit & loss"
        description="Posted income and expense · thin view"
      />

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}
      {plQuery.isPending || !data ? (
        <Alert variant="muted" className="mt-8">
          Loading profit &amp; loss…
        </Alert>
      ) : (
        <>
          <section className="mt-8">
            <h2 className="m-0 text-sm font-semibold text-[var(--color-muted)]">
              Income
            </h2>
            {data.income.length === 0 ? (
              <div className="mt-3">
                <EmptyState
                  title="No income yet"
                  description="Posted sales will show here."
                />
              </div>
            ) : (
              <AdminList>
                {data.income.map((r) => (
                  <AdminListRow
                    key={r.accountId}
                    className="gap-3 px-4 py-2.5 text-sm"
                  >
                    <Link
                      to={`/c/${companySlug}/gl/accounts/${r.accountId}`}
                      className="min-w-0 text-inherit no-underline hover:underline"
                    >
                      <span className="font-mono text-xs text-[var(--color-muted)]">
                        {r.code}
                      </span>{" "}
                      {r.name}
                    </Link>
                    <span className="shrink-0 font-mono">
                      {data.currencyCode} {moneyDisplay(r.amount)}
                    </span>
                  </AdminListRow>
                ))}
              </AdminList>
            )}
            <div className="mt-2 flex justify-between text-sm font-semibold">
              <span>Total income</span>
              <span className="font-mono">
                {data.currencyCode} {moneyDisplay(data.totalIncome)}
              </span>
            </div>
          </section>

          <section className="mt-8">
            <h2 className="m-0 text-sm font-semibold text-[var(--color-muted)]">
              Expenses
            </h2>
            {data.expense.length === 0 ? (
              <div className="mt-3">
                <EmptyState
                  title="No expenses yet"
                  description="Posted costs will show here."
                />
              </div>
            ) : (
              <AdminList>
                {data.expense.map((r) => (
                  <AdminListRow
                    key={r.accountId}
                    className="gap-3 px-4 py-2.5 text-sm"
                  >
                    <Link
                      to={`/c/${companySlug}/gl/accounts/${r.accountId}`}
                      className="min-w-0 text-inherit no-underline hover:underline"
                    >
                      <span className="font-mono text-xs text-[var(--color-muted)]">
                        {r.code}
                      </span>{" "}
                      {r.name}
                    </Link>
                    <span className="shrink-0 font-mono">
                      {data.currencyCode} {moneyDisplay(r.amount)}
                    </span>
                  </AdminListRow>
                ))}
              </AdminList>
            )}
            <div className="mt-2 flex justify-between text-sm font-semibold">
              <span>Total expense</span>
              <span className="font-mono">
                {data.currencyCode} {moneyDisplay(data.totalExpense)}
              </span>
            </div>
          </section>

          <div className="mt-8 flex justify-between border-t border-[var(--color-border)] pt-4 text-base font-bold">
            <span>Net income</span>
            <span className="font-mono">
              {data.currencyCode} {moneyDisplay(data.netIncome)}
            </span>
          </div>
        </>
      )}
    </PageFrame>
  );
}
